/**
 * Generic searchable picker used by the inspection wizard for
 * Holding / Contractor / Inspector lookups. Same pattern as
 * `new-registration-dialog.tsx`'s inline pickers but extracted so the
 * three wizard slots stay terse.
 *
 * - Renders a SearchInput; results dropdown appears once typed >=2 chars
 * - On result click → onSelect(uuid, label) → dropdown closes
 * - When a value is selected, renders a "chip" with the label + Clear
 * - Caller owns the search query state + the result list (so it can
 *   tune debouncing / caching per source)
 */

import { useState, useRef, useEffect } from 'react'
import { Check, X, Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { SearchInput } from '@/shared/components/ui/search-input'

export interface SearchablePickerProps<T> {
  label: React.ReactNode
  /** Caller-controlled search query string. */
  search: string
  onSearchChange: (value: string) => void
  /** Result rows; expected to be the typeahead response. */
  results: T[] | undefined
  isLoading?: boolean
  /** Currently-selected uuid, or '' for nothing. */
  selectedUuid: string
  /** Display string for the selected value (caller looks it up). */
  selectedLabel?: string
  /**
   * Optional second line shown under the selected label inside the chip.
   * Use for location / role / disambiguating context that's helpful at
   * a glance once a value is picked.
   */
  selectedSecondary?: string
  /** Click handler when a result is chosen. */
  onSelect: (uuid: string, label: string) => void
  /** Caller signals "clear the selected value". */
  onClear: () => void
  /** Map a result to its uuid + display rows. */
  getOptionUuid: (item: T) => string
  getOptionPrimary: (item: T) => string
  getOptionSecondary?: (item: T) => string | undefined
  placeholder?: string
  helperText?: React.ReactNode
  required?: boolean
  /**
   * Filter-bar mode: no label above, control height matches the h-8
   * selects it sits beside. Default false (form mode, 44px target).
   */
  compact?: boolean
}

export function SearchablePicker<T>({
  label,
  search,
  onSearchChange,
  results,
  isLoading,
  selectedUuid,
  selectedLabel,
  selectedSecondary,
  onSelect,
  onClear,
  getOptionUuid,
  getOptionPrimary,
  getOptionSecondary,
  placeholder = 'Type to search…',
  helperText,
  required,
  compact = false,
}: SearchablePickerProps<T>) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close the dropdown on outside-click.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handler)
      return () => document.removeEventListener('mousedown', handler)
    }
    return undefined
  }, [open])

  if (selectedUuid) {
    return (
      <div className="flex flex-col gap-1.5">
        {!compact && (
          <Label>
            {label}
            {required && (
              <span className="text-[var(--status-error,#991b1b)]"> *</span>
            )}
          </Label>
        )}
        <div
          className={`flex items-center justify-between gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] px-3 ${
            compact ? 'h-8 py-0' : 'py-2 min-h-[44px]'
          }`}
        >
          <div className="flex min-w-0 items-start gap-2 text-sm text-[var(--ink)]">
            <Check
              size={14}
              className="mt-0.5 shrink-0 text-[var(--status-ok,#1c5a48)]"
            />
            <div className="flex min-w-0 flex-col">
              <span className="line-clamp-1">
                {selectedLabel || selectedUuid}
              </span>
              {selectedSecondary && (
                <span className="line-clamp-1 text-[11px] text-[var(--ink-3)]">
                  {selectedSecondary}
                </span>
              )}
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onClear}
            className="min-h-[32px] shrink-0 text-[var(--ink-3)]"
          >
            <X size={12} className="mr-1" />
            Clear
          </Button>
        </div>
        {helperText && (
          <p className="text-[11px] text-[var(--ink-3)]">{helperText}</p>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      {!compact && (
        <Label>
          {label}
          {required && (
            <span className="text-[var(--status-error,#991b1b)]"> *</span>
          )}
        </Label>
      )}
      <div className="relative">
        <SearchInput
          value={search}
          onChange={(v) => {
            onSearchChange(v)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className={compact ? 'h-8 min-h-0' : 'min-h-[44px]'}
        />
        {open && search.trim().length >= 2 && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-[280px] overflow-y-auto rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] shadow-lg">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs text-[var(--ink-3)]">
                <Loader2 size={12} className="animate-spin" />
                Searching…
              </div>
            ) : (results ?? []).length === 0 ? (
              <p className="py-6 text-center text-xs text-[var(--ink-3)]">
                No matches for "{search}".
              </p>
            ) : (
              <ul className="flex flex-col">
                {(results ?? []).map((item) => {
                  const uuid = getOptionUuid(item)
                  const primary = getOptionPrimary(item)
                  const secondary = getOptionSecondary?.(item)
                  return (
                    <li key={uuid}>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          onSelect(uuid, primary)
                          onSearchChange('')
                          setOpen(false)
                        }}
                        className="flex h-auto w-full flex-col items-start gap-0.5 rounded-none border-b-[0.5px] border-b-[var(--rule)] px-3 py-2 text-left font-normal last:border-b-0 hover:bg-[var(--bg-light)]"
                      >
                        <span className="text-sm text-[var(--ink)]">{primary}</span>
                        {secondary && (
                          <span className="text-[11px] text-[var(--ink-3)]">
                            {secondary}
                          </span>
                        )}
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}
      </div>
      {helperText && (
        <p className="text-[11px] text-[var(--ink-3)]">{helperText}</p>
      )}
    </div>
  )
}
